const normalizeText = value => String(value || '').trim();
const normalizeKey = value =>
  normalizeText(value)
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
const normalizeCount = value => {
  const num = Number(value);
  return Number.isFinite(num) && num > 0 ? Math.round(num) : 0;
};
const normalizeBatchLevel = value => {
  const level = normalizeText(value).toLowerCase();
  return ['blocked', 'watch', 'ready', 'idle'].includes(level) ? level : 'idle';
};
const DEFAULT_ROLLBACK_SESSION_LABEL = 'rollback publish session';
const buildRollbackSessionId = prefix => {
  const normalizedPrefix = normalizeKey(prefix) || 'rollback';
  return `${normalizedPrefix}-${Date.now().toString(36)}-${Math.random()
    .toString(36)
    .slice(2, 8)}`;
};
const normalizeRollbackSessionStep = value => {
  const num = Number(value);
  return Number.isFinite(num) && num > 0 ? Math.round(num) : 1;
};
const normalizeRollbackJourneyHopKind = value => {
  const normalized = normalizeText(value).toLowerCase();
  return normalized || '';
};
export const humanizeRollbackJourneyHopKind = value => {
  const normalized = normalizeRollbackJourneyHopKind(value);
  switch (normalized) {
    case 'evidence_note':
      return 'evidence note';
    case 'rollback':
      return 'rollback';
    case 'publish':
      return 'publish';
    case 'initial_publish':
      return 'initial publish';
    case 'rollout_publish':
      return 'inbox rollout';
    case 'clear_rollouts':
      return 'clear rollout overrides';
    default:
      return normalized.replace(/[_-]+/g, ' ');
  }
};
const deriveOperatorNoteHopKind = row => {
  const explicit = normalizeRollbackJourneyHopKind(row.hop_kind || row.hopKind);
  if (explicit) return explicit;
  const kind = normalizeRollbackJourneyHopKind(row.kind);
  if (kind === 'rollback_followup') return 'evidence_note';
  return kind || 'evidence_note';
};
const deriveOperatorNoteActionLabel = row => {
  const explicit = normalizeText(
    row.actor_action_label || row.actorActionLabel
  );
  if (explicit) return explicit;
  const recommended = normalizeText(
    row.recommended_action_label || row.recommendedActionLabel
  );
  if (recommended) return recommended;
  return humanizeRollbackJourneyHopKind(deriveOperatorNoteHopKind(row));
};
const deriveOperatorNoteWorkspaceLabel = row =>
  normalizeText(
    row.workspace_label ||
      row.workspaceLabel ||
      row.source ||
      row.session_origin ||
      row.sessionOrigin
  );
const normalizeOperatorNoteHistoryEntry = entry => {
  const row = entry && typeof entry === 'object' ? entry : {};
  const body = normalizeText(row.body || row.note);
  if (!body) return null;

  return {
    kind: normalizeText(row.kind) || 'rollback_followup',
    body,
    source: normalizeText(row.source),
    created_at: normalizeText(row.created_at || row.createdAt),
    actor_id: Number(row.actor_id || row.actorId) || null,
    actor_name: normalizeText(row.actor_name || row.actorName),
    target_version_label: normalizeText(
      row.target_version_label || row.targetVersionLabel
    ),
    compare_version_label: normalizeText(
      row.compare_version_label || row.compareVersionLabel
    ),
    recommended_action_label: normalizeText(
      row.recommended_action_label || row.recommendedActionLabel
    ),
    confidence_label: normalizeText(row.confidence_label || row.confidenceLabel),
    session_id: normalizeText(row.session_id || row.sessionId),
    session_label: normalizeText(row.session_label || row.sessionLabel),
    session_step: normalizeRollbackSessionStep(
      row.session_step || row.sessionStep || 1
    ),
    session_origin: normalizeText(row.session_origin || row.sessionOrigin),
    next_workspace: normalizeText(row.next_workspace || row.nextWorkspace),
    hop_kind: deriveOperatorNoteHopKind(row),
    actor_action_label: deriveOperatorNoteActionLabel(row),
    workspace_label: deriveOperatorNoteWorkspaceLabel(row),
  };
};
const normalizeOperatorNoteHistory = value =>
  Array.isArray(value)
    ? value.map(normalizeOperatorNoteHistoryEntry).filter(Boolean).slice(-25)
    : [];
const buildOperatorNoteFallbackEntry = record => {
  const row = record && typeof record === 'object' ? record : {};
  const body = normalizeText(row.operator_note || row.operatorNote);
  if (!body) return null;

  return {
    kind: 'rollback_followup',
    body,
    source: normalizeText(row.operator_note_source || row.operatorNoteSource),
    created_at: normalizeText(
      row.operator_note_updated_at || row.operatorNoteUpdatedAt
    ),
    actor_id: null,
    actor_name: '',
    target_version_label: normalizeText(
      row.target_version_label || row.targetVersionLabel
    ),
    compare_version_label: normalizeText(
      row.compare_version_label ||
        row.compareVersionLabel ||
        row.previous_version_label ||
        row.previousVersionLabel
    ),
    recommended_action_label: normalizeText(
      row.recommended_action_label || row.recommendedActionLabel
    ),
    confidence_label: normalizeText(row.confidence_label || row.confidenceLabel),
    session_id: normalizeText(row.session_id || row.sessionId),
    session_label: normalizeText(row.session_label || row.sessionLabel),
    session_step: normalizeRollbackSessionStep(
      row.session_step || row.sessionStep || 1
    ),
    session_origin: normalizeText(row.session_origin || row.sessionOrigin),
    next_workspace: normalizeText(row.next_workspace || row.nextWorkspace),
    hop_kind: deriveOperatorNoteHopKind(row),
    actor_action_label: deriveOperatorNoteActionLabel(row),
    workspace_label: deriveOperatorNoteWorkspaceLabel(row),
  };
};
const operatorNoteTimelineSortValue = entry =>
  Date.parse(normalizeText(entry?.created_at)) || 0;
const operatorNoteTimelineEntryKey = entry =>
  [
    normalizeText(entry?.body),
    normalizeText(entry?.source),
    normalizeText(entry?.created_at),
    normalizeText(entry?.target_version_label),
    normalizeText(entry?.compare_version_label),
    normalizeText(entry?.session_id),
    normalizeText(entry?.session_step),
    normalizeText(entry?.hop_kind),
  ].join('::');
const humanizeEvidenceKind = value => {
  const normalized = normalizeText(value).toLowerCase();
  switch (normalized) {
    case 'release':
      return 'release snapshot';
    case 'baseline':
      return 'batch baseline';
    default:
      return normalized || 'evidence';
  }
};
const buildSessionSourceRecord = source => {
  if (source?.record) {
    return {
      kind: normalizeText(source.kind || source.recordKind),
      record: source.record,
    };
  }
  return {
    kind: '',
    record: source,
  };
};
const buildSessionEvidenceRefKey = ref =>
  [
    normalizeText(ref?.recordKind),
    normalizeText(ref?.recordKey),
    normalizeText(ref?.entryKey),
  ].join('::');
const buildSessionEvidenceRefLabel = ref => {
  const kind = humanizeEvidenceKind(ref?.recordKind);
  const recordLabel = normalizeText(ref?.recordLabel);
  return recordLabel ? `${kind}: ${recordLabel}` : kind;
};
const buildSessionEntryGroupKey = item => {
  const session = normalizeRollbackSessionContext(item?.session || {});
  if (session.sessionId) {
    return `${session.sessionId}::${session.sessionStep || 1}`;
  }
  return `legacy::${normalizeText(item?.entryKey)}`;
};
const versionRefLabel = ({ versionId = null, versionNumber = null } = {}) => {
  const normalizedNumber = Number(versionNumber);
  if (Number.isFinite(normalizedNumber) && normalizedNumber > 0) {
    return `#${normalizedNumber}`;
  }
  const normalizedId = Number(versionId);
  if (Number.isFinite(normalizedId) && normalizedId > 0) {
    return `ID ${normalizedId}`;
  }
  return '';
};
const humanizeRollbackFocus = value => {
  const normalized = normalizeText(value).toLowerCase();
  switch (normalized) {
    case 'release':
      return 'release readiness';
    case 'channels':
      return 'channel strategy';
    case 'inbox_controls':
      return 'inbox controls';
    case 'business_hours':
      return 'business-hours strategy';
    case 'region_compliance':
      return 'region compliance';
    default:
      return normalizeText(value);
  }
};
const humanizeRollbackReason = value => {
  const normalized = normalizeText(value);
  if (!normalized) return '';
  return normalized.replace(/[_-]+/g, ' ');
};
const humanizeRollbackSessionLabel = value => {
  const normalized = normalizeText(value);
  return normalized ? normalized.replace(/[_-]+/g, ' ') : '';
};

export const normalizeRollbackSessionContext = ({
  sessionId = '',
  sessionLabel = '',
  sessionStep = 1,
  sessionOrigin = '',
  nextWorkspace = '',
} = {}) => ({
  sessionId: normalizeText(sessionId),
  sessionLabel: humanizeRollbackSessionLabel(sessionLabel),
  sessionStep: normalizeRollbackSessionStep(sessionStep),
  sessionOrigin: normalizeText(sessionOrigin),
  nextWorkspace: normalizeText(nextWorkspace),
});

export const rollbackSessionContextFromEntry = entry =>
  normalizeRollbackSessionContext({
    sessionId: entry?.session_id || entry?.sessionId,
    sessionLabel: entry?.session_label || entry?.sessionLabel,
    sessionStep: entry?.session_step || entry?.sessionStep,
    sessionOrigin: entry?.session_origin || entry?.sessionOrigin,
    nextWorkspace: entry?.next_workspace || entry?.nextWorkspace,
  });

export const ensureRollbackSessionContext = ({
  current = null,
  sourceWorkspace = '',
  defaultLabel = DEFAULT_ROLLBACK_SESSION_LABEL,
  nextWorkspace = '',
} = {}) => {
  const normalizedCurrent = normalizeRollbackSessionContext({
    sessionId: current?.sessionId || current?.session_id,
    sessionLabel: current?.sessionLabel || current?.session_label || defaultLabel,
    sessionStep: current?.sessionStep || current?.session_step || 1,
    sessionOrigin: current?.sessionOrigin || current?.session_origin || sourceWorkspace,
    nextWorkspace: current?.nextWorkspace || current?.next_workspace || nextWorkspace,
  });
  if (normalizedCurrent.sessionId) {
    return {
      ...normalizedCurrent,
      nextWorkspace: normalizeText(nextWorkspace) || normalizedCurrent.nextWorkspace,
    };
  }
  return normalizeRollbackSessionContext({
    sessionId: buildRollbackSessionId(sourceWorkspace || 'rollback'),
    sessionLabel: defaultLabel,
    sessionStep: 1,
    sessionOrigin: sourceWorkspace,
    nextWorkspace,
  });
};

export const advanceRollbackSessionContext = ({
  current = null,
  sourceWorkspace = '',
  nextWorkspace = '',
  defaultLabel = DEFAULT_ROLLBACK_SESSION_LABEL,
} = {}) => {
  const ensured = ensureRollbackSessionContext({
    current,
    sourceWorkspace,
    defaultLabel,
    nextWorkspace,
  });
  const hadSessionId = Boolean(normalizeText(current?.sessionId || current?.session_id));
  return normalizeRollbackSessionContext({
    sessionId: ensured.sessionId,
    sessionLabel: ensured.sessionLabel,
    sessionStep: hadSessionId ? ensured.sessionStep + 1 : ensured.sessionStep,
    sessionOrigin: ensured.sessionOrigin || sourceWorkspace,
    nextWorkspace,
  });
};

export const buildRollbackBreadcrumbBadges = ({
  source = '',
  focus = '',
  intentLabel = '',
  targetVersionLabel = '',
  compareVersionLabel = '',
  confidenceLabel = '',
  reason = '',
  nextLabel = '',
  sessionLabel = '',
  sessionStep = '',
  sessionOrigin = '',
} = {}) => {
  const badges = [];
  const normalizedSource = normalizeText(source);
  const normalizedFocus = humanizeRollbackFocus(focus);
  const normalizedIntentLabel = normalizeText(intentLabel);
  const normalizedTargetVersionLabel = normalizeText(targetVersionLabel);
  const normalizedCompareVersionLabel = normalizeText(compareVersionLabel);
  const normalizedConfidenceLabel = normalizeText(confidenceLabel);
  const normalizedReason = humanizeRollbackReason(reason);
  const normalizedNextLabel = normalizeText(nextLabel);
  const normalizedSessionLabel = normalizeText(sessionLabel);
  const normalizedSessionStep = normalizeRollbackSessionStep(sessionStep || 1);
  const normalizedSessionOrigin = normalizeText(sessionOrigin);

  if (normalizedSource) {
    badges.push({ label: `from ${normalizedSource}`, tone: 'muted' });
  }
  if (normalizedSessionLabel) {
    badges.push({ label: normalizedSessionLabel, tone: 'muted' });
  }
  if (normalizedSessionOrigin) {
    badges.push({ label: `origin ${normalizedSessionOrigin}`, tone: 'muted' });
  }
  if (normalizedSessionLabel || normalizedSessionOrigin) {
    badges.push({ label: `step ${normalizedSessionStep}`, tone: 'muted' });
  }
  if (normalizedFocus) {
    badges.push({ label: normalizedFocus, tone: 'muted' });
  }
  if (normalizedIntentLabel) {
    badges.push({ label: normalizedIntentLabel, tone: 'primary' });
  }
  if (normalizedTargetVersionLabel) {
    badges.push({
      label: normalizedCompareVersionLabel
        ? `${normalizedTargetVersionLabel} vs ${normalizedCompareVersionLabel}`
        : normalizedTargetVersionLabel,
      tone: 'primary',
    });
  }
  if (normalizedConfidenceLabel) {
    badges.push({ label: normalizedConfidenceLabel, tone: 'muted' });
  }
  if (normalizedNextLabel) {
    badges.push({ label: `next ${normalizedNextLabel}`, tone: 'muted' });
  }
  if (normalizedReason) {
    badges.push({ label: normalizedReason, tone: 'muted' });
  }
  return badges;
};

export const resolveOperatorNoteTimelineEntries = record => {
  const history = normalizeOperatorNoteHistory(
    record?.operator_note_history || record?.operatorNoteHistory
  );
  if (history.length) return history;
  const fallback = buildOperatorNoteFallbackEntry(record);
  return fallback ? [fallback] : [];
};

export const latestOperatorNoteTimelineEntries = ({
  record = null,
  limit = 3,
} = {}) => {
  const rows = resolveOperatorNoteTimelineEntries(record);
  const normalizedLimit = Math.max(Number(limit) || 0, 0);
  if (!normalizedLimit) return [];
  return rows.slice(-normalizedLimit).reverse();
};

export const mergeOperatorNoteTimelineEntries = ({
  records = [],
  limit = 6,
} = {}) => {
  const normalizedLimit = Math.max(Number(limit) || 0, 0);
  const seen = new Set();
  const rows = [];
  records.forEach(record => {
    resolveOperatorNoteTimelineEntries(record).forEach(entry => {
      const key = operatorNoteTimelineEntryKey(entry);
      if (!key || seen.has(key)) return;
      seen.add(key);
      rows.push(entry);
    });
  });
  rows.sort((left, right) => operatorNoteTimelineSortValue(left) - operatorNoteTimelineSortValue(right));
  if (!normalizedLimit) return [];
  return rows.slice(-normalizedLimit).reverse();
};

export const groupOperatorNoteTimelineSessions = ({
  sources = [],
  limitSessions = 4,
  limitEntriesPerSession = 4,
} = {}) => {
  const rows = [];
  sources.forEach(source => {
    const { kind, record } = buildSessionSourceRecord(source);
    if (!record || typeof record !== 'object') return;
    const recordKind = normalizeText(kind);
    const recordLabel = normalizeText(
      record?.bundle_title ||
        record?.bundleTitle ||
        record?.title ||
        record?.baseline_key ||
        record?.baselineKey ||
        record?.snapshot_key ||
        record?.snapshotKey
    );
    const recordKey = normalizeKey(
      record?.id || record?.snapshot_key || record?.snapshotKey || record?.baseline_key || record?.baselineKey || recordLabel
    );
    resolveOperatorNoteTimelineEntries(record).forEach(entry => {
      rows.push({
        entry,
        entryKey: operatorNoteTimelineEntryKey(entry),
        session: rollbackSessionContextFromEntry(entry),
        createdSortValue: operatorNoteTimelineSortValue(entry),
        recordKind,
        recordKey,
        recordLabel,
        record,
      });
    });
  });
  if (!rows.length) return [];

  const groupedEntries = new Map();
  rows.forEach(item => {
    const groupKey = buildSessionEntryGroupKey(item);
    const existing = groupedEntries.get(groupKey);
    const evidenceRef = {
      recordKind: item.recordKind,
      recordKey: item.recordKey,
      recordLabel: item.recordLabel,
      record: item.record,
      entryKey: item.entryKey,
      label: buildSessionEvidenceRefLabel(item),
    };
    if (!existing) {
      groupedEntries.set(groupKey, {
        ...item,
        evidenceRefs: evidenceRef.recordKey ? [evidenceRef] : [],
      });
      return;
    }
    const seenRefs = new Set(existing.evidenceRefs.map(buildSessionEvidenceRefKey));
    const nextRefKey = buildSessionEvidenceRefKey(evidenceRef);
    if (nextRefKey && !seenRefs.has(nextRefKey)) {
      existing.evidenceRefs.push(evidenceRef);
    }
    if (item.createdSortValue > existing.createdSortValue) {
      existing.entry = item.entry;
      existing.createdSortValue = item.createdSortValue;
      existing.session = item.session;
    }
  });

  const sessions = new Map();
  Array.from(groupedEntries.values()).forEach(item => {
    const session = normalizeRollbackSessionContext(item.session || {});
    const sessionKey = session.sessionId || `legacy-${normalizeKey(item.entryKey) || 'rollback-note'}`;
    const existing = sessions.get(sessionKey);
    if (!existing) {
      sessions.set(sessionKey, {
        sessionId: session.sessionId,
        sessionLabel: session.sessionLabel || (session.sessionId ? DEFAULT_ROLLBACK_SESSION_LABEL : 'legacy rollback note'),
        sessionOrigin: session.sessionOrigin,
        firstCreatedAt: normalizeText(item.entry?.created_at),
        latestCreatedAt: normalizeText(item.entry?.created_at),
        firstSortValue: item.createdSortValue,
        latestSortValue: item.createdSortValue,
        stepCount: 0,
        totalEntries: 0,
        entries: [item],
        evidenceRefs: [...item.evidenceRefs],
      });
      return;
    }
    existing.entries.push(item);
    if (item.createdSortValue < existing.firstSortValue) {
      existing.firstSortValue = item.createdSortValue;
      existing.firstCreatedAt = normalizeText(item.entry?.created_at);
    }
    if (item.createdSortValue > existing.latestSortValue) {
      existing.latestSortValue = item.createdSortValue;
      existing.latestCreatedAt = normalizeText(item.entry?.created_at);
    }
    const seenRefs = new Set(existing.evidenceRefs.map(buildSessionEvidenceRefKey));
    item.evidenceRefs.forEach(ref => {
      const refKey = buildSessionEvidenceRefKey(ref);
      if (refKey && !seenRefs.has(refKey)) {
        seenRefs.add(refKey);
        existing.evidenceRefs.push(ref);
      }
    });
  });

  const normalizedLimitSessions = Math.max(Number(limitSessions) || 0, 0);
  const normalizedLimitEntries = Math.max(Number(limitEntriesPerSession) || 0, 0);
  return Array.from(sessions.values())
    .map(session => {
      const sortedEntries = [...session.entries].sort((left, right) => {
        const leftStep = normalizeRollbackSessionStep(
          left?.session?.sessionStep || left?.entry?.session_step
        );
        const rightStep = normalizeRollbackSessionStep(
          right?.session?.sessionStep || right?.entry?.session_step
        );
        if (leftStep !== rightStep) return leftStep - rightStep;
        return left.createdSortValue - right.createdSortValue;
      });
      const latestEntry = sortedEntries[sortedEntries.length - 1] || null;
      return {
        ...session,
        latestEntry,
        stepCount: sortedEntries.reduce((max, item) => {
          const nextStep = normalizeRollbackSessionStep(
            item?.session?.sessionStep || item?.entry?.session_step
          );
          return Math.max(max, nextStep);
        }, 0),
        totalEntries: sortedEntries.length,
        entries: normalizedLimitEntries
          ? sortedEntries.slice(0, normalizedLimitEntries)
          : sortedEntries,
        hiddenEntryCount: normalizedLimitEntries
          ? Math.max(sortedEntries.length - normalizedLimitEntries, 0)
          : 0,
      };
    })
    .sort((left, right) => right.latestSortValue - left.latestSortValue)
    .slice(0, normalizedLimitSessions || undefined);
};

export const buildOperatorNoteSessionReplaySummary = session => {
  const row = session && typeof session === 'object' ? session : {};
  const parts = [];
  if (Number(row.stepCount || 0) > 0) {
    parts.push(`${row.stepCount} step${Number(row.stepCount) === 1 ? '' : 's'}`);
  }
  if (Array.isArray(row.evidenceRefs) && row.evidenceRefs.length) {
    parts.push(
      `${row.evidenceRefs.length} evidence target${row.evidenceRefs.length === 1 ? '' : 's'}`
    );
  }
  const context = buildOperatorNoteTimelineContextLabel(row.latestEntry?.entry);
  if (context) parts.push(context);
  return parts.join(' • ');
};

export const groupRollbackSessionJourney = ({
  noteSources = [],
  ledgerEntries = [],
  limitSessions = 4,
  limitItemsPerSession = 6,
} = {}) => {
  const noteSessions = groupOperatorNoteTimelineSessions({
    sources: noteSources,
    limitSessions: 0,
    limitEntriesPerSession: 0,
  });
  const sessions = new Map();
  const ensureSessionBucket = ({
    session = null,
    fallbackKey = '',
    fallbackLabel = '',
  } = {}) => {
    const normalizedSession = normalizeRollbackSessionContext(session || {});
    const sessionKey =
      normalizedSession.sessionId ||
      `legacy-${normalizeKey(fallbackKey || fallbackLabel || 'rollback-session')}`;
    if (!sessions.has(sessionKey)) {
      sessions.set(sessionKey, {
        sessionKey,
        sessionId: normalizedSession.sessionId,
        sessionLabel:
          normalizedSession.sessionLabel ||
          fallbackLabel ||
          (normalizedSession.sessionId
            ? DEFAULT_ROLLBACK_SESSION_LABEL
            : 'legacy rollback session'),
        sessionOrigin: normalizedSession.sessionOrigin,
        firstCreatedAt: '',
        latestCreatedAt: '',
        firstSortValue: 0,
        latestSortValue: 0,
        stepCount: 0,
        totalItems: 0,
        noteCount: 0,
        ledgerCount: 0,
        items: [],
        evidenceRefs: [],
      });
    }
    return sessions.get(sessionKey);
  };
  const mergeEvidenceRefs = (bucket, refs = []) => {
    const seen = new Set((bucket.evidenceRefs || []).map(buildSessionEvidenceRefKey));
    refs.forEach(ref => {
      const refKey = buildSessionEvidenceRefKey(ref);
      if (!refKey || seen.has(refKey)) return;
      seen.add(refKey);
      bucket.evidenceRefs.push(ref);
    });
  };
  const updateSessionWindow = (bucket, sortValue, createdAt) => {
    if (!bucket.firstCreatedAt || sortValue < bucket.firstSortValue) {
      bucket.firstSortValue = sortValue;
      bucket.firstCreatedAt = createdAt;
    }
    if (!bucket.latestCreatedAt || sortValue > bucket.latestSortValue) {
      bucket.latestSortValue = sortValue;
      bucket.latestCreatedAt = createdAt;
    }
  };

  noteSessions.forEach(session => {
    session.entries.forEach(item => {
      const step = normalizeRollbackSessionStep(
        item?.session?.sessionStep || item?.entry?.session_step
      );
      const currentSession = normalizeRollbackSessionContext({
        sessionId: session.sessionId,
        sessionLabel: session.sessionLabel,
        sessionStep: step,
        sessionOrigin:
          item?.session?.sessionOrigin || item?.entry?.session_origin || session.sessionOrigin,
        nextWorkspace:
          item?.entry?.next_workspace || item?.entry?.nextWorkspace || '',
      });
      const bucket = ensureSessionBucket({
        session: currentSession,
        fallbackKey: item?.entryKey,
        fallbackLabel: session.sessionLabel,
      });
      const journeyItem = {
        itemType: 'evidence_note',
        itemKey: `note:${item?.entryKey || item?.entry?.created_at || bucket.items.length}`,
        sortValue: Number(item?.createdSortValue || 0),
        createdAt: normalizeText(item?.entry?.created_at),
        step,
        session: currentSession,
        hopKind: normalizeRollbackJourneyHopKind(item?.entry?.hop_kind) || 'evidence_note',
        actorActionLabel:
          normalizeText(item?.entry?.actor_action_label) ||
          deriveOperatorNoteActionLabel(item?.entry || {}),
        workspaceLabel:
          normalizeText(item?.entry?.workspace_label) ||
          deriveOperatorNoteWorkspaceLabel(item?.entry || {}),
        summary: normalizeText(item?.entry?.body),
        entry: item?.entry || null,
        evidenceRefs: item?.evidenceRefs || [],
      };
      bucket.noteCount += 1;
      bucket.stepCount = Math.max(bucket.stepCount, step);
      bucket.items.push(journeyItem);
      mergeEvidenceRefs(bucket, journeyItem.evidenceRefs);
      updateSessionWindow(bucket, journeyItem.sortValue, journeyItem.createdAt);
    });
  });

  Array(ledgerEntries).forEach(row => {
    if (!row || typeof row !== 'object') return;
    const session = normalizeRollbackSessionContext({
      sessionId: row.session_id || row.sessionId,
      sessionLabel: row.session_label || row.sessionLabel,
      sessionStep: row.session_step || row.sessionStep,
      sessionOrigin: row.session_origin || row.sessionOrigin,
      nextWorkspace: row.next_workspace || row.nextWorkspace,
    });
    const step = normalizeRollbackSessionStep(session.sessionStep || 1);
    const createdAt = normalizeText(row.created_at || row.createdAt);
    const sortValue = Date.parse(createdAt) || 0;
    const evidenceRefs = [
      {
        recordKind: 'release',
        recordKey: normalizeText(row.release_snapshot_key || row.releaseSnapshotKey),
        recordLabel: normalizeText(row.bundle_title || row.focus_label),
        entryKey: `ledger:${row.id || createdAt}:release`,
      },
      {
        recordKind: 'baseline',
        recordKey: normalizeText(row.batch_baseline_key || row.batchBaselineKey),
        recordLabel: normalizeText(row.batch_baseline_title || row.bundle_title),
        entryKey: `ledger:${row.id || createdAt}:baseline`,
      },
    ].filter(ref => ref.recordKey);
    const bucket = ensureSessionBucket({
      session: {
        ...session,
        sessionStep: step,
      },
      fallbackKey: row.id || createdAt || row.event_type,
      fallbackLabel: session.sessionLabel || humanizeRollbackJourneyHopKind(row.event_type),
    });
    const journeyItem = {
      itemType: 'ledger_hop',
      itemKey: `ledger:${row.id || createdAt || bucket.items.length}`,
      sortValue,
      createdAt,
      step,
      session: {
        ...session,
        sessionStep: step,
      },
      hopKind: normalizeRollbackJourneyHopKind(row.event_type) || 'publish',
      actorActionLabel: humanizeRollbackJourneyHopKind(row.event_type) || 'ledger hop',
      workspaceLabel: normalizeText(row.source || session.sessionOrigin || 'Deploy'),
      summary: normalizeText(row.note),
      row,
      evidenceRefs,
      targetVersionLabel: versionRefLabel({
        versionId: row.target_version_id,
        versionNumber: row.target_version_number,
      }),
      compareVersionLabel: versionRefLabel({
        versionId: row.previous_published_version_id,
        versionNumber: row.previous_published_version_number,
      }),
    };
    bucket.ledgerCount += 1;
    bucket.stepCount = Math.max(bucket.stepCount, step);
    bucket.items.push(journeyItem);
    mergeEvidenceRefs(bucket, evidenceRefs);
    updateSessionWindow(bucket, journeyItem.sortValue, journeyItem.createdAt);
  });

  const normalizedLimitSessions = Math.max(Number(limitSessions) || 0, 0);
  const normalizedLimitItems = Math.max(Number(limitItemsPerSession) || 0, 0);
  return Array.from(sessions.values())
    .map(bucket => {
      const sortedItems = [...bucket.items].sort((left, right) => {
        if (left.step !== right.step) return left.step - right.step;
        if (left.sortValue !== right.sortValue) return left.sortValue - right.sortValue;
        if (left.itemType === right.itemType) return 0;
        return left.itemType === 'evidence_note' ? -1 : 1;
      });
      const latestItem = sortedItems[sortedItems.length - 1] || null;
      return {
        ...bucket,
        latestItem,
        totalItems: sortedItems.length,
        items: normalizedLimitItems ? sortedItems.slice(0, normalizedLimitItems) : sortedItems,
        hiddenItemCount: normalizedLimitItems
          ? Math.max(sortedItems.length - normalizedLimitItems, 0)
          : 0,
      };
    })
    .sort((left, right) => right.latestSortValue - left.latestSortValue)
    .slice(0, normalizedLimitSessions || undefined);
};

export const buildRollbackSessionJourneySummary = session => {
  const row = session && typeof session === 'object' ? session : {};
  const parts = [];
  if (Number(row.stepCount || 0) > 0) {
    parts.push(`${row.stepCount} step${Number(row.stepCount) === 1 ? '' : 's'}`);
  }
  if (Number(row.noteCount || 0) > 0) {
    parts.push(`${row.noteCount} note${Number(row.noteCount) === 1 ? '' : 's'}`);
  }
  if (Number(row.ledgerCount || 0) > 0) {
    parts.push(`${row.ledgerCount} ledger hop${Number(row.ledgerCount) === 1 ? '' : 's'}`);
  }
  if (Array.isArray(row.evidenceRefs) && row.evidenceRefs.length) {
    parts.push(
      `${row.evidenceRefs.length} evidence target${row.evidenceRefs.length === 1 ? '' : 's'}`
    );
  }
  return parts.join(' • ');
};

export const buildRollbackSessionJourneyItemBadges = item => {
  const row = item && typeof item === 'object' ? item : {};
  if (row.itemType === 'ledger_hop') {
    return buildRollbackBreadcrumbBadges({
      source: row.workspaceLabel,
      intentLabel: row.actorActionLabel,
      targetVersionLabel: row.targetVersionLabel,
      compareVersionLabel: row.compareVersionLabel,
      sessionLabel: row.session?.sessionLabel,
      sessionStep: row.step,
      sessionOrigin: row.session?.sessionOrigin,
      nextLabel: row.session?.nextWorkspace,
    });
  }
  return buildRollbackBreadcrumbBadges({
    source: row.entry?.source,
    intentLabel: row.actorActionLabel || row.entry?.actor_action_label,
    targetVersionLabel: row.entry?.target_version_label || row.entry?.targetVersionLabel,
    compareVersionLabel:
      row.entry?.compare_version_label || row.entry?.compareVersionLabel,
    confidenceLabel: row.entry?.confidence_label || row.entry?.confidenceLabel,
    sessionLabel: row.session?.sessionLabel,
    sessionStep: row.step,
    sessionOrigin: row.session?.sessionOrigin,
    nextLabel: row.session?.nextWorkspace,
  });
};

export const buildRollbackJourneyItemAction = item => {
  const row = item && typeof item === 'object' ? item : {};
  const noteEntry = row.itemType === 'ledger_hop' ? {} : row.entry || {};
  const ledgerEntry = row.itemType === 'ledger_hop' ? row.row || {} : {};
  const releaseRef =
    Array.isArray(row.evidenceRefs)
      ? row.evidenceRefs.find(ref => ref.recordKind === 'release')
      : null;
  const baselineRef =
    Array.isArray(row.evidenceRefs)
      ? row.evidenceRefs.find(ref => ref.recordKind === 'baseline')
      : null;
  const releaseRecord = releaseRef?.record || {};
  const baselineRecord = baselineRef?.record || {};
  const targetVersionLabel =
    normalizeText(
      noteEntry.target_version_label ||
        noteEntry.targetVersionLabel ||
        row.targetVersionLabel
    ) ||
    versionRefLabel({
      versionId: ledgerEntry.target_version_id,
      versionNumber: ledgerEntry.target_version_number,
    });
  const compareVersionLabel =
    normalizeText(
      noteEntry.compare_version_label ||
        noteEntry.compareVersionLabel ||
        noteEntry.previous_version_label ||
        noteEntry.previousVersionLabel ||
        row.compareVersionLabel
    ) ||
    versionRefLabel({
      versionId: ledgerEntry.previous_published_version_id,
      versionNumber: ledgerEntry.previous_published_version_number,
    });
  const intentLabel =
    normalizeText(
      row.actorActionLabel ||
        noteEntry.actor_action_label ||
        noteEntry.actorActionLabel ||
        noteEntry.recommended_action_label ||
        noteEntry.recommendedActionLabel
    ) ||
    humanizeRollbackJourneyHopKind(row.hopKind || ledgerEntry.event_type || noteEntry.hop_kind);
  return {
    kind: normalizeText(row.hopKind || ledgerEntry.event_type || noteEntry.hop_kind),
    label: intentLabel,
    intent_label: intentLabel,
    bundle_label: normalizeText(
      releaseRecord.bundle_title ||
        baselineRecord.bundle_title ||
        ledgerEntry.bundle_title ||
        noteEntry.bundle_title ||
        ledgerEntry.focus_label ||
        releaseRecord.focus_label ||
        baselineRecord.focus_label
    ),
    bundle_title: normalizeText(
      releaseRecord.bundle_title ||
        baselineRecord.bundle_title ||
        ledgerEntry.bundle_title ||
        noteEntry.bundle_title
    ),
    focus_label: normalizeText(
      releaseRecord.focus_label ||
        baselineRecord.focus_label ||
        ledgerEntry.focus_label ||
        noteEntry.focus_label
    ),
    recommendation_code: normalizeText(
      releaseRecord.recommendation_code ||
        baselineRecord.recommendation_code ||
        ledgerEntry.recommendation_code ||
        noteEntry.recommendation_code
    ),
    target_version_label: targetVersionLabel,
    compare_version_label: compareVersionLabel,
    previous_version_label: compareVersionLabel,
    release_snapshot_id: releaseRecord.id || ledgerEntry.release_snapshot_id || null,
    release_snapshot_key: normalizeText(
      releaseRecord.snapshot_key || ledgerEntry.release_snapshot_key || releaseRef?.recordKey
    ),
    batch_baseline_id: baselineRecord.id || ledgerEntry.batch_baseline_id || null,
    batch_baseline_key: normalizeText(
      baselineRecord.baseline_key || ledgerEntry.batch_baseline_key || baselineRef?.recordKey
    ),
    batch_baseline_title: normalizeText(
      baselineRecord.title || ledgerEntry.batch_baseline_title || baselineRef?.recordLabel
    ),
    session_id: row.session?.sessionId,
    session_label: row.session?.sessionLabel,
    session_step: row.step || row.session?.sessionStep,
    session_origin: row.session?.sessionOrigin,
    session_item_key: normalizeText(row.itemKey),
    next_workspace: row.session?.nextWorkspace,
  };
};

export const buildOperatorNoteTimelineContextLabel = entry => {
  const row = entry && typeof entry === 'object' ? entry : {};
  const parts = [];
  const targetVersionLabel = normalizeText(
    row.target_version_label || row.targetVersionLabel
  );
  const compareVersionLabel = normalizeText(
    row.compare_version_label || row.compareVersionLabel
  );
  if (targetVersionLabel) {
    parts.push(
      compareVersionLabel
        ? `${targetVersionLabel} vs ${compareVersionLabel}`
        : targetVersionLabel
    );
  }
  if (normalizeText(row.recommended_action_label || row.recommendedActionLabel)) {
    parts.push(
      normalizeText(row.recommended_action_label || row.recommendedActionLabel)
    );
  }
  if (normalizeText(row.confidence_label || row.confidenceLabel)) {
    parts.push(normalizeText(row.confidence_label || row.confidenceLabel));
  }
  return parts.join(' • ');
};

export const buildOperatorNoteTimelineBadges = entry =>
  buildRollbackBreadcrumbBadges({
    source: entry?.source,
    intentLabel: entry?.recommended_action_label || entry?.recommendedActionLabel,
    targetVersionLabel: entry?.target_version_label || entry?.targetVersionLabel,
    compareVersionLabel: entry?.compare_version_label || entry?.compareVersionLabel,
    confidenceLabel: entry?.confidence_label || entry?.confidenceLabel,
    sessionLabel: entry?.session_label || entry?.sessionLabel,
    sessionStep: entry?.session_step || entry?.sessionStep,
    sessionOrigin: entry?.session_origin || entry?.sessionOrigin,
    nextLabel: entry?.next_workspace || entry?.nextWorkspace,
  });

export const buildRollbackEvidenceCompareBundle = ({
  record = null,
  ownerWorkspace = 'deploy',
  ownerActionTitle = '',
} = {}) => {
  const row = record && typeof record === 'object' ? record : {};
  const title = normalizeText(row.bundle_title || row.bundleTitle || row.title);
  const focusLabel = normalizeText(row.focus_label || row.focusLabel);
  const strategyGroupTitle = normalizeText(
    row.strategy_group_title || row.strategyGroupTitle
  );
  const recommendationCode = normalizeText(
    row.recommendation_code || row.recommendationCode
  );
  return {
    enabled: Boolean(title || focusLabel || recommendationCode),
    title,
    focusLabel,
    strategyGroupTitle,
    recommendationCode,
    ownerWorkspace: normalizeText(ownerWorkspace),
    ownerActionTitle:
      normalizeText(ownerActionTitle) ||
      normalizeText(row.owner_action_title || row.ownerActionTitle) ||
      'Rollback evidence timeline',
    guidanceEditor: normalizeText(row.guidance_editor || row.guidanceEditor),
    escalationFocus: normalizeText(row.escalation_focus || row.escalationFocus),
    hotspotSummary: normalizeText(row.hotspot_summary || row.hotspotSummary),
    resolvedSummary: normalizeText(row.resolved_summary || row.resolvedSummary),
    escalatedSummary: normalizeText(row.escalated_summary || row.escalatedSummary),
    validationSummary: normalizeText(
      row.validation_summary || row.validationSummary
    ),
    stage: normalizeText(row.stage) || 'validation',
    rollbackTrigger: normalizeText(
      row.rollback_trigger || row.rollbackTrigger
    ),
  };
};

export const buildOperatorNoteTimelineVersionDialogContext = ({
  record = null,
  entry = null,
  fallbackIntent = 'Rollback evidence timeline',
  selectedVersionRole = 'target',
  sessionContext = null,
  sessionItemKey = '',
} = {}) => {
  const row = entry && typeof entry === 'object' ? entry : {};
  const compareBundle = buildRollbackEvidenceCompareBundle({
    record,
    ownerWorkspace: 'deploy',
    ownerActionTitle:
      buildOperatorNoteTimelineContextLabel(row) || normalizeText(fallbackIntent),
  });
  const intentLabel =
    buildOperatorNoteTimelineContextLabel(row) || normalizeText(fallbackIntent);
  const targetVersionLabel = normalizeText(
    row.target_version_label || row.targetVersionLabel
  );
  const compareVersionLabel = normalizeText(
    row.compare_version_label || row.compareVersionLabel
  );
  const session = ensureRollbackSessionContext({
    current: sessionContext || rollbackSessionContextFromEntry(row),
    sourceWorkspace: 'Deploy',
  });
  return {
    intent_label: intentLabel,
    bundle_label: compareBundle.title || 'Rollback evidence',
    target_version_label: targetVersionLabel,
    compare_version_label: compareVersionLabel,
    selected_version_role: normalizeText(selectedVersionRole) || 'target',
    session_id: session.sessionId,
    session_label: session.sessionLabel,
    session_step: session.sessionStep,
    session_origin: session.sessionOrigin,
    session_item_key: normalizeText(sessionItemKey),
  };
};

export const buildReleaseReviewSnapshotKey = bundle => {
  const row = bundle && typeof bundle === 'object' ? bundle : {};
  return [
    normalizeText(
      row.snapshot_key || row.snapshotKey || row.recommendation_code || row.recommendationCode
    ),
    normalizeText(
      row.strategy_group_title || row.strategyGroupTitle
    ),
    normalizeText(row.focus_label || row.focusLabel),
    normalizeText(row.bundle_title || row.bundleTitle || row.title),
  ].join('::');
};

export const normalizeReleaseReviewSnapshot = snapshot => {
  const row = snapshot && typeof snapshot === 'object' ? snapshot : {};
  const snapshotKey = buildReleaseReviewSnapshotKey(row);
  const reviewedAt = normalizeText(row.reviewed_at || row.reviewedAt);
  const status = normalizeText(row.status).toLowerCase();

  if (!snapshotKey || /^:+$/.test(snapshotKey.replace(/[^:]/g, ''))) {
    return null;
  }

  return {
    id: row.id || null,
    snapshot_key: snapshotKey,
    bundle_title: normalizeText(row.bundle_title || row.bundleTitle || row.title),
    focus_label: normalizeText(row.focus_label || row.focusLabel),
    strategy_group_title: normalizeText(
      row.strategy_group_title || row.strategyGroupTitle
    ),
    recommendation_code: normalizeText(
      row.recommendation_code || row.recommendationCode
    ),
    owner_workspace: normalizeText(row.owner_workspace || row.ownerWorkspace),
    owner_action_title: normalizeText(
      row.owner_action_title || row.ownerActionTitle
    ),
    guidance_editor: normalizeText(row.guidance_editor || row.guidanceEditor),
    escalation_focus: normalizeText(row.escalation_focus || row.escalationFocus),
    hotspot_summary: normalizeText(row.hotspot_summary || row.hotspotSummary),
    resolved_summary: normalizeText(row.resolved_summary || row.resolvedSummary),
    escalated_summary: normalizeText(row.escalated_summary || row.escalatedSummary),
    validation_summary: normalizeText(
      row.validation_summary || row.validationSummary
    ),
    stage: normalizeText(row.stage).toLowerCase() || 'validation',
    rollback_trigger: normalizeText(
      row.rollback_trigger || row.rollbackTrigger || row.validationRollbackTrigger
    ),
    status:
      ['reviewing', 'released', 'rolled_back'].includes(status) ? status : 'reviewing',
    note: normalizeText(row.note),
    operator_note: normalizeText(row.operator_note || row.operatorNote),
    operator_note_source: normalizeText(
      row.operator_note_source || row.operatorNoteSource
    ),
    operator_note_updated_at: normalizeText(
      row.operator_note_updated_at || row.operatorNoteUpdatedAt
    ),
    operator_note_history: normalizeOperatorNoteHistory(
      row.operator_note_history || row.operatorNoteHistory
    ),
    source: normalizeText(row.source),
    confidence_level: normalizeText(
      row.confidence_level || row.confidenceLevel
    ).toLowerCase(),
    confidence_runs: Number(row.confidence_runs || row.confidenceRuns) || 0,
    confidence_title: normalizeText(
      row.confidence_title || row.confidenceTitle
    ),
    batch_baseline_key: normalizeText(
      row.batch_baseline_key || row.batchBaselineKey
    ),
    batch_set_key: normalizeText(row.batch_set_key || row.batchSetKey),
    batch_title: normalizeText(row.batch_title || row.batchTitle),
    batch_ran_at: normalizeText(row.batch_ran_at || row.batchRanAt),
    batch_overall_level: normalizeBatchLevel(
      row.batch_overall_level || row.batchOverallLevel
    ),
    batch_total_cases: normalizeCount(
      row.batch_total_cases || row.batchTotalCases
    ),
    batch_ready_cases: normalizeCount(
      row.batch_ready_cases || row.batchReadyCases
    ),
    batch_watch_cases: normalizeCount(
      row.batch_watch_cases || row.batchWatchCases
    ),
    batch_blocked_cases: normalizeCount(
      row.batch_blocked_cases || row.batchBlockedCases
    ),
    batch_top_blocker: normalizeText(
      row.batch_top_blocker || row.batchTopBlocker
    ),
    batch_top_watch_signal: normalizeText(
      row.batch_top_watch_signal || row.batchTopWatchSignal
    ),
    reviewed_at: reviewedAt,
  };
};

export const normalizeReleaseReviewSnapshots = snapshots =>
  Array(snapshots)
    .map(normalizeReleaseReviewSnapshot)
    .filter(Boolean)
    .filter(
      (snapshot, index, rows) =>
        rows.findIndex(item => item.snapshot_key === snapshot.snapshot_key) === index
    );

export const mergeReleaseReviewSnapshots = ({
  snapshots = [],
  snapshot = null,
  limit = 20,
} = {}) => {
  const normalizedSnapshot = normalizeReleaseReviewSnapshot(snapshot);
  const normalizedRows = normalizeReleaseReviewSnapshots(snapshots);
  if (!normalizedSnapshot) return normalizedRows.slice(0, Number(limit) || 20);

  return [
    normalizedSnapshot,
    ...normalizedRows.filter(
      row => row.snapshot_key !== normalizedSnapshot.snapshot_key
    ),
  ].slice(0, Number(limit) || 20);
};

export const findReleaseReviewSnapshot = ({ snapshots = [], bundle = {} } = {}) => {
  const snapshotKey = buildReleaseReviewSnapshotKey(bundle);
  if (!snapshotKey || /^:+$/.test(snapshotKey.replace(/[^:]/g, ''))) return null;

  return (
    normalizeReleaseReviewSnapshots(snapshots).find(
      snapshot => snapshot.snapshot_key === snapshotKey
    ) || null
  );
};

export const buildBatchBaselineKey = baseline => {
  const row = baseline && typeof baseline === 'object' ? baseline : {};
  return (
    normalizeText(row.baseline_key || row.baselineKey || row.set_key || row.setKey) ||
    normalizeKey(row.title || row.batch_title || row.batchTitle) ||
    normalizeText(row.bundle_snapshot_key || row.bundleSnapshotKey) ||
    'ad-hoc-batch-baseline'
  );
};

export const normalizeBatchBaseline = baseline => {
  const row = baseline && typeof baseline === 'object' ? baseline : {};
  const baselineKey = buildBatchBaselineKey(row);
  const title = normalizeText(row.title || row.batch_title || row.batchTitle);
  const hasBundleIdentity = [
    row.recommendation_code || row.recommendationCode,
    row.strategy_group_title || row.strategyGroupTitle,
    row.focus_label || row.focusLabel,
  ].some(value => normalizeText(value));
  const bundleSnapshotKey = normalizeText(
    row.bundle_snapshot_key || row.bundleSnapshotKey
  )
    || (hasBundleIdentity ? buildReleaseReviewSnapshotKey(row) : '');

  if (!baselineKey || !title) return null;

  return {
    id: row.id || null,
    baseline_key: baselineKey,
    set_key: normalizeText(row.set_key || row.setKey),
    title,
    source: normalizeText(row.source),
    overall_level: normalizeBatchLevel(
      row.overall_level || row.overallLevel || row.confidence_level
    ),
    confidence_level: normalizeBatchLevel(
      row.confidence_level || row.confidenceLevel || row.overall_level
    ),
    confidence_runs: normalizeCount(
      row.confidence_runs || row.confidenceRuns || row.total_cases || row.totalCases
    ),
    confidence_title: normalizeText(
      row.confidence_title || row.confidenceTitle
    ),
    total_cases: normalizeCount(row.total_cases || row.totalCases),
    ready_cases: normalizeCount(row.ready_cases || row.readyCases),
    watch_cases: normalizeCount(row.watch_cases || row.watchCases),
    blocked_cases: normalizeCount(row.blocked_cases || row.blockedCases),
    primary_inbox_id: normalizeCount(
      row.primary_inbox_id || row.primaryInboxId || row.inbox_id || row.inboxId
    ),
    top_blocker: normalizeText(row.top_blocker || row.topBlocker),
    top_watch_signal: normalizeText(
      row.top_watch_signal || row.topWatchSignal
    ),
    ran_at: normalizeText(row.ran_at || row.ranAt || row.reviewed_at),
    bundle_snapshot_key:
      bundleSnapshotKey && !/^:+$/.test(bundleSnapshotKey.replace(/[^:]/g, ''))
        ? bundleSnapshotKey
        : '',
    bundle_title: normalizeText(row.bundle_title || row.bundleTitle || row.title),
    focus_label: normalizeText(row.focus_label || row.focusLabel),
    strategy_group_title: normalizeText(
      row.strategy_group_title || row.strategyGroupTitle
    ),
    recommendation_code: normalizeText(
      row.recommendation_code || row.recommendationCode
    ),
    owner_workspace: normalizeText(row.owner_workspace || row.ownerWorkspace),
    owner_action_title: normalizeText(
      row.owner_action_title || row.ownerActionTitle
    ),
    guidance_editor: normalizeText(row.guidance_editor || row.guidanceEditor),
    escalation_focus: normalizeText(row.escalation_focus || row.escalationFocus),
    operator_note: normalizeText(row.operator_note || row.operatorNote),
    operator_note_source: normalizeText(
      row.operator_note_source || row.operatorNoteSource
    ),
    operator_note_updated_at: normalizeText(
      row.operator_note_updated_at || row.operatorNoteUpdatedAt
    ),
    operator_note_history: normalizeOperatorNoteHistory(
      row.operator_note_history || row.operatorNoteHistory
    ),
  };
};

export const normalizeBatchBaselines = baselines =>
  Array(baselines)
    .map(normalizeBatchBaseline)
    .filter(Boolean)
    .filter(
      (baseline, index, rows) =>
        rows.findIndex(item => item.baseline_key === baseline.baseline_key) ===
        index
    );

export const mergeBatchBaselines = ({
  baselines = [],
  baseline = null,
  limit = 20,
} = {}) => {
  const normalizedBaseline = normalizeBatchBaseline(baseline);
  const normalizedRows = normalizeBatchBaselines(baselines);
  if (!normalizedBaseline) return normalizedRows.slice(0, Number(limit) || 20);

  return [
    normalizedBaseline,
    ...normalizedRows.filter(
      row => row.baseline_key !== normalizedBaseline.baseline_key
    ),
  ].slice(0, Number(limit) || 20);
};

export const findBatchBaseline = ({
  baselines = [],
  baselineKey = '',
  setKey = '',
  bundle = {},
} = {}) => {
  const normalizedRows = normalizeBatchBaselines(baselines);
  const directKey = normalizeText(baselineKey);
  const directSetKey = normalizeText(setKey);
  const bundleSnapshotKey = buildReleaseReviewSnapshotKey(bundle);

  return (
    normalizedRows.find(row => {
      if (directKey && row.baseline_key === directKey) return true;
      if (directSetKey && row.set_key === directSetKey) return true;
      if (
        bundleSnapshotKey &&
        !/^:+$/.test(bundleSnapshotKey.replace(/[^:]/g, '')) &&
        row.bundle_snapshot_key === bundleSnapshotKey
      ) {
        return true;
      }
      return false;
    }) || null
  );
};
